/**
 * Parse a date string from the API as UTC.
 * The backend stores dates in UTC but serializes DateTime without the 'Z' suffix.
 * Without this, JavaScript interprets the date as local time.
 */
export function parseUtcDate(iso: string): Date {
  if (iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso)) {
    return new Date(iso)
  }
  return new Date(iso + 'Z')
}

/**
 * Format a UTC ISO date string to a human-readable local date+time.
 * Example: "29 de marzo de 2026, 02:05 a.m."
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = parseUtcDate(iso)
  return d.toLocaleDateString('es-PE', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Format a UTC ISO date string to a human-readable local date (no time).
 * Example: "29 de marzo de 2026"
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = parseUtcDate(iso)
  return d.toLocaleDateString('es-PE', { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * Antigüedad en palabras, escalonada por magnitud: "12 min", "3 h", "5 d",
 * "2 meses", "1 año".
 *
 * Existe porque el tiempo desde el último reporte GPS se imprimía siempre en
 * minutos: un traslado que quedó abierto hace meses mostraba un número de cinco
 * cifras, ilegible y sin escala. Se salta la unidad cuando no aporta —a partir de
 * un día nadie necesita las horas— y redondea hacia abajo, que es como se lee una
 * antigüedad: algo de 90 minutos lleva "1 h", no "2 h".
 */
export function formatElapsed(minutes: number): string {
  const min = Math.max(0, Math.floor(minutes))
  if (min < 1) return 'ahora'
  if (min < 60) return `${min} min`

  const horas = Math.floor(min / 60)
  if (horas < 24) return `${horas} h`

  const dias = Math.floor(horas / 24)
  if (dias < 31) return `${dias} d`

  const meses = Math.floor(dias / 30)
  if (meses < 12) return meses === 1 ? '1 mes' : `${meses} meses`

  const anios = Math.floor(dias / 365)
  return anios === 1 ? '1 año' : `${anios} años`
}
